import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

export const CommentMediaAsset_Asset = graphql(`
  fragment CommentMediaAsset_Asset on Asset @_unmask {
    __typename
    id
    mimeType
    thumbnail
    source
    type
    ... on ImageAsset {
      image {
        altText
        width
        height
        isAnimated
      }
    }
    ... on VideoAsset {
      video {
        width
        height
      }
    }
  }
`)

/**
 * The lastReplies selection shared by every document that materializes a
 * comment's replies: the Comments list query, the CommentThread query, and
 * the ReplyToComment mutation (whose response is written through the Apollo
 * cache). Sharing one fragment is load-bearing, not cosmetic — if a document
 * selected fewer fields than another one reads, its cache write would leave
 * that reader's data partial, and Apollo silently refetches partial queries,
 * which drops status-filtered comments from the list mid-session.
 *
 * Both `CommentData_Comment` and the Comments list query select this fragment,
 * so it must live in a module that imports neither of them. `graphql()` builds
 * each document during module evaluation, so an import cycle between those two
 * throws a temporal-dead-zone `ReferenceError` before either is defined.
 */
export const CommentLastReply_LastReply = graphql(
  `
    fragment CommentLastReply_LastReply on LastReply @_unmask {
      id
      text
      contentType
      organizationId
      channelId
      publicUrl
      serviceType
      publishedAt
      parentId
      status
      isOwnReply
      metadata {
        ... on GoogleBusinessCommentMetadata {
          userRating
        }
      }
      publishingStatus {
        status
        errorMessage
      }
      author {
        username
        name
        avatar
      }
      assets {
        ...CommentMediaAsset_Asset
      }
      reactions {
        type
      }
    }
  `,
  [CommentMediaAsset_Asset],
)

/**
 * A single server reply on a comment (`comment.lastReplies`). Derived from the
 * shared `CommentLastReply_LastReply` selection rather than the codegen
 * `LastReply` entity, so the reply rows read the same shape the producers
 * materialize.
 */
export type LastReply = FragmentOf<typeof CommentLastReply_LastReply>
